import React, { useContext, useRef, useState } from 'react'
import { Formik } from 'formik'
import { MatchedPlaidTransaction, URL_REGEX, titleCase } from '@ecountabl/lib'
import * as yup from 'yup'
import { View, Animated, Easing, Text, TextInput, StyleSheet } from 'react-native'
import { textStyles } from './style/style'
import { US_STATES } from './utility'
import api from './api-client'
import { ThemedTextInput } from './utility/input/textinputs/themed-text-input'
import { ThemedDropdown } from './utility/input/misc/themed-dropdown'
import { ThemedCheckBox } from './utility/input/misc/themed-checkbox'
import { CustomActionSheetScrollView } from './utility/CustomActionSheet'
import { ThemedButton } from './utility/input/buttons'
import { ThemeContext } from './utility/context/ThemeContext'
import KeyboardAvoidingScrollView from './utility/keyboard-avoiding-scrollview'
import SuccessAnimation from './utility/success-animation'
import ErrorAnimation from './utility/error-animation'
import Analytics from './utility/analytics'

enum SubmitStep {
  NotSubmitted,
  Submitting,
  Submitted,
  InputError,
  ServerError
}

const validationSchema = yup.object().shape({
  name: yup.string().required().trim().min(3).label("Business Name"),
  link: yup.string().matches(
    new RegExp(`(${URL_REGEX.source})|(^@[a-zA-Z0-9_]{1,15}$)`,'i'),
    { message: "Invalid URL or @Handle" }
  ),
  isLocal: yup.boolean(),
  city: yup.string().trim().min(3).when(
    'isLocal', {
      is: true,
      then: yup.string().required(),
      otherwise: yup.string().notRequired()
    }).label("City"),
  state: yup.string().oneOf(US_STATES.map(state => state.abbreviation)).when(
    'isLocal', {
      is: true,
      then: yup.string().required(),
      otherwise: yup.string().notRequired()
    }).label("State"),
  userFreeFormInput: yup.string()
})

interface FormValues {
  name: string;
  link: string;
  isLocal: boolean;
  city: string;
  state: string;
  userFreeFormInput: string;
}

type AddCompanyProps = {
  tx?: MatchedPlaidTransaction;
  defaultName?: string;
  closeSheet: () => void;
  isLocal?: boolean;
}

const AddCompanyForm: React.FC<AddCompanyProps> = ({
  tx, defaultName, closeSheet, isLocal
}: AddCompanyProps) => {
  const theme = useContext(ThemeContext).theme
  const { textMain } = theme.colors

  const [submitStep, setSubmitStep] = useState(SubmitStep.NotSubmitted)
  const [serverErrors, setServerErrors] = useState<string[]>([])

  const localBusinessRowHeight = useRef(new Animated.Value(isLocal ? 1 : 0)).current
  const maxRowHeight = localBusinessRowHeight.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 100] // needs to be > content height
  })

  const handleIsLocalChange = (isLocal: boolean) => {
    Animated.timing(localBusinessRowHeight, {
      toValue: isLocal ? 1 : 0,
      duration: 200,
      easing: Easing.linear,
      useNativeDriver: false
    }).start()
  }

  const inputRefs = {
    name: useRef<TextInput>(null),
    link: useRef<TextInput>(null),
    city: useRef<TextInput>(null),
    userFreeFormInput: useRef<TextInput>(null)
  }

  return (
    <>
      <Formik<FormValues>
        initialValues={{
          name: defaultName ? defaultName : (tx ? titleCase(tx.name) : ''),
          link: '',
          isLocal: isLocal || false,
          city: tx ? tx.location?.city : '',
          state: tx ? tx.location?.region : '',
          userFreeFormInput: ''
        }}
        onSubmit={(company) => {
          // won't be fired if there are validation errors
          setSubmitStep(SubmitStep.Submitting)
          let succeeded = false
          const link = company.link || ''
          // if social handle seen, it is not a website
          const has_social_handle = /^@[a-zA-Z0-9_]{1,15}$/.test(link.trim())
          const has_website = !!link && !has_social_handle

          Analytics.track('user_suggest_new_business', {
            name: company.name,
            isLocal: !!company.isLocal,
            city: company.city,
            state: company.state,
            has_website,
            has_social_handle,
            has_freeform: !!company.userFreeFormInput,
            originTxid: tx?._id,
            ratings_local: !!company.isLocal,
          })
          api.submitCompany({ company, tx }).then(() => {
            setSubmitStep(SubmitStep.Submitted)
            succeeded = true
          }).catch(ex => {
            setServerErrors(ex.errors || [])
            setSubmitStep(ex.status !== 400 ? SubmitStep.ServerError : SubmitStep.InputError)
          }).finally(() => {
            Analytics.track('add_company_submitted', {
              success: succeeded,
            })
          })
        }}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          handleChange, values, errors, touched, handleSubmit, setFieldValue
        }) => {
          const hasValues = Object.keys(values).some(key => !!values[key] === true)

          if (submitStep === SubmitStep.Submitted) {
            return <>
              <CustomActionSheetScrollView bounces={false}>
                <View style={{gap: 8}}>
                  <Text style={[textStyles.h2, {color: textMain}]}>Add a Business</Text>
                  
                  <View style={{alignItems: "center", gap: 8}}>
                    <SuccessAnimation size={250} />
                    <Text style={[textStyles.h2, {color: textMain}]}>
                      Success!
                    </Text>
                    <Text style={[textStyles.body, {color: textMain, textAlign: 'center'}]}>
                      {`Thanks for the submission! We'll check out ${values.name} as soon as possible!`}
                    </Text>
                    <Text style={[textStyles.body, {color: textMain, textAlign: 'center'}]}>
                      Keep an eye out for them in the app!
                    </Text>
                  </View>
                </View>
              </CustomActionSheetScrollView>
              <View style={styles.submitButtonWrapper}> 
                <ThemedButton
                  text="Close"
                  onPress={closeSheet}
                  variant="primary"
                  hapticLevel="Soft"
                />
              </View>
            </>
          } else if (submitStep === SubmitStep.ServerError) {
            return <>
              <CustomActionSheetScrollView bounces={false}>
                <View style={{gap: 8}}>
                  <Text style={[textStyles.h2, {color: textMain}]}>Add a Business</Text>
                  <View style={{alignItems: "center", gap: 8}}>
                    <ErrorAnimation size={250} />
                    <Text style={[textStyles.h2, {color: textMain}]}>
                      Error!
                    </Text>
                    <Text style={[textStyles.body, {color: textMain}]}>
                      There was an error submitting info for {values.name || "this business"}.
                    </Text>
                    <Text style={[textStyles.body, {color: textMain}]}>
                      Please try again later.
                    </Text>
                  </View>
                </View>
              </CustomActionSheetScrollView>
              <View style={styles.submitButtonWrapper}>
                <ThemedButton
                  text="Close"
                  onPress={closeSheet}
                  variant="primary"
                  hapticLevel="Soft"
                />
              </View>
            </>
          }
          return (
            <View style={{flex: 1, justifyContent: "space-between", height: "100%"}}>
              <KeyboardAvoidingScrollView
                contentContainerStyle={{
                  gap: 16,
                  paddingHorizontal: 16
                }}
              >
                <View style={{gap: 8}}>
                  <Text style={[textStyles.h2, {marginBottom: 5, color: textMain}]}>
                    Add a Business
                  </Text>
                  <Text style={[textStyles.body, {marginBottom: 5, color: textMain}]}>
                    We are always looking to put more businesses into Impact Banking and on the radar{' '}
                    of our users. If you have a business that you think would be a good fit, please{' '}
                    fill out the form below and we&apos;ll be sure to give it a look!
                  </Text>
                </View>
                
                <ThemedTextInput
                  ref={inputRefs.name}
                  value={values.name}
                  onChange={(text) => handleChange('name')(text)}
                  label="Business Name"
                  required
                  placeholder="Jane's Bakery"
                  textInputProps={{
                    autoComplete: "off",
                    autoCorrect: false,
                    returnKeyType: "next",
                    onSubmitEditing: () => {
                      values.isLocal ?
                        inputRefs.city.current?.focus()
                        : inputRefs.link.current?.focus()
                    }
                  }}
                  errorText={touched.name ? errors.name : undefined}
                  disabled={submitStep === SubmitStep.Submitting}
                />
                <View>
                  <ThemedCheckBox
                    label='Local Business'
                    checked={values.isLocal}
                    onChange={() => {
                      setFieldValue('isLocal', !values.isLocal)
                      handleIsLocalChange(!values.isLocal)
                    }}
                    showInactiveLabel
                    disabled={submitStep === SubmitStep.Submitting}
                  />
                  <Animated.View style={{flexDirection: "row", gap: 5, maxHeight: maxRowHeight, overflow: "hidden"}}>
                    <View style={{flex: 1}}>
                      <ThemedTextInput
                        ref={inputRefs.city}
                        value={values.city}
                        onChange={(text) => handleChange('city')(text)}
                        label="City"
                        required
                        placeholder="Seattle"
                        textInputProps={{
                          autoComplete: "off",
                          returnKeyType: "next",
                          onSubmitEditing: () => {
                            // Blur when next input is a dropdown
                            inputRefs.city.current?.blur()
                          }
                        }}
                        errorText={touched.city ? errors.city : undefined}
                        disabled={submitStep === SubmitStep.Submitting}
                      />
                    </View>
                    <View style={{flex: 0.4, maxWidth: 100}}>
                      <ThemedDropdown
                        label="State"
                        placeholder='State'
                        required
                        data={US_STATES}
                        value={values.state}
                        onChange={(val) => handleChange('state')(val)}
                        displayTextField="abbreviation"
                        valueField="abbreviation"
                        errorText={touched.state ? errors.state : undefined}
                        disabled={submitStep === SubmitStep.Submitting}
                      />
                    </View>
                  </Animated.View>
                </View>
                
                <ThemedTextInput
                  ref={inputRefs.link}
                  value={values.link}
                  onChange={(text) => handleChange('link')(text)}
                  label="Website or @Handle"
                  placeholder="www.janesbakery.com"
                  textInputProps={{
                    autoComplete: "off",
                    autoCorrect: false,
                    autoCapitalize: "none",
                    returnKeyType: "next",
                    onSubmitEditing: () => {
                      inputRefs.userFreeFormInput.current?.focus()
                    }
                  }}
                  errorText={touched.link ? errors.link : undefined}
                  disabled={submitStep === SubmitStep.Submitting}
                />

                <ThemedTextInput
                  ref={inputRefs.userFreeFormInput}
                  value={values.userFreeFormInput}
                  onChange={(text) => {
                    if (text === "\n") {
                      return
                    }
                    handleChange('userFreeFormInput')(text)
                  }}
                  label="What should we know about this business?"
                  placeholder="We love their cookies!"
                  textInputProps={{
                    numberOfLines: 3,
                    autoComplete: "off",
                    autoCorrect: false,
                    multiline: true,
                    returnKeyType: "done",
                    style: {
                      maxHeight: 100
                    },
                    onKeyPress: (e) => {
                      if (e.nativeEvent.key === "Enter") {
                        e.preventDefault()
                        inputRefs.userFreeFormInput.current?.blur()
                      }
                    }
                  }}
                  disabled={submitStep === SubmitStep.Submitting}
                />

                {
                submitStep === SubmitStep.InputError && serverErrors.length > 0 ? 
                <View style={{paddingTop: 10}}>
                  {
                    serverErrors.map((err, i) => (
                      <Text key={i} style={[textStyles.subscript, {color: "red"}]}>{err}</Text>
                    ))
                  }
                </View> : null
                }
              </KeyboardAvoidingScrollView>
              <View style={styles.submitButtonWrapper}>
                <ThemedButton
                  text={hasValues ? "Submit Business" : "Close"}
                  onPress={hasValues ? handleSubmit : closeSheet}
                  loading={submitStep === SubmitStep.Submitting}
                  variant="primary"
                />
              </View>
            </View>
          )
        }}
      </Formik>
    </>
  )
}

export default AddCompanyForm

const styles = StyleSheet.create({
  submitButtonWrapper: {
    paddingHorizontal: 16,
    paddingTop: 16
  }
})